import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  forwardRef,
  Input,
  HostBinding,
  Injector,
  Output,
  EventEmitter
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputBase } from '../_shared/base.class';
import { Subject } from 'rxjs';
import { filter, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'sc-input',
  templateUrl: './input.component.html',
  styleUrls: ['../_shared/inputs.scss', './input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ],
  // tslint:disable-next-line: no-host-metadata-property
  host: {
    class: 'sc-input'
  }
})
export class InputComponent extends InputBase implements OnInit {
  @Input() placeholder: string;
  @Input() type = 'text';
  @Input() readonly: boolean;
  @Input() autocomplete = 'on';
  @Input() maxlength: number;
  @Input() min: number;
  @Input() max: number;
  @Input() numStep = 1;
  @Input() mask: string;
  @Input() inputmode: string;

  @Input() @HostBinding('class.arrows') arrows: boolean;

  keyUp: Subject<KeyboardEvent> = new Subject<KeyboardEvent>();
  @Output() onupdate: EventEmitter<string> = new EventEmitter<string>();

  constructor(injector: Injector) {
    super(injector);
  }

  onInit() {
    this.initKeyup();
  }

  initKeyup(): void {
    this.keyUp
      .pipe(
        takeUntil(this.destroy$),
        filter((event: KeyboardEvent) => !['Tab'].includes(event.key)),
        map(event => (event.target as HTMLInputElement).value),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.value = this.prepateValue(value);
        this.onChange(this.value);
        this.onupdate.emit(this.value);
        this.cdr.detectChanges();
      });
  }

  writeValue(delta: any): void {
    this._value = delta;
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.input && this.input.nativeElement) {
          this.renderer.setProperty(this.input.nativeElement, 'value', delta);
        }
      });
    });
  }

  onFocusEvent(focused: boolean, e: Event) {
    this.setActive(!!focused || !!(e.target as HTMLInputElement).value);
    this.onfocus.emit(!!focused);
  }

  // getMask(event: any) {
  //   if (typeof this.mask !== 'function') {
  //     return;
  //   }

  //   const elem = event.target;
  //   elem.value = this.mask(elem.value);
  // }

  private prepateValue(value: any) {
    if (this.type === 'number') {
      return +value;
    }
    return value;
  }

  // plus/minus buttons

  changeValue(target: number): void {
    if (this.type !== 'number') {
      return;
    }

    let value = this.prepateValue(this.value) + (target * this.numStep);
    if (this.numStep < 1) {
      value = +value.toFixed(2)
    } else {
      value = Math.round(value);
    }

    if (value < this.min) {
      value = this.min;
    }
    if (value > this.max) {
      value = this.max;
    }
    this.writeValue(value);
    this.onChange(this.value);
    this.onupdate.emit(this.value);
    this.cdr.detectChanges();
  }

  get canIncrement(): boolean {
    return (
      this.max === undefined ||
      (this.type === 'number' && this.value < this.max)
    );
  }

  get canDecrement(): boolean {
    return (
      this.min === undefined ||
      (this.type === 'number' && this.value > this.min)
    );
  }
}
